import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import TableWrapper from "../../core/BaseComponents/TableWrapper/TableWrapper";
import dayjs from "dayjs";
import { Tooltip, Autocomplete, TextField, CircularProgress } from "@mui/material";
import { MdModeEditOutline } from "react-icons/md";
import { callAPI } from "../../../axios";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import DeleteDefaultWelcomeMessage from "./DeleteDefaultWelcomeMessage";
import { GUEST_NAME_PLACEHOLDER } from "../WelcomeMessageConfig/welcomeMessageTemplateUtils";
import Custom_Paingation from "../../core/BaseComponents/Custom_Paingation";
import { ScrollToTop, getChainIdLS, isValidRole } from "../../../utils/utils";
import { useSelector } from "react-redux";

const MAX_CHARS = 70;
const MIN_GUEST_NAME_CHARS = 15;

const getPlaceholderCount = (template) =>
  template?.includes(GUEST_NAME_PLACEHOLDER)
    ? template.split(GUEST_NAME_PLACEHOLDER).length - 1
    : 0;

// Preview length budget: each $$GUEST_NAME$$ reserves MIN_GUEST_NAME_CHARS (not all 14 literal chars).
const getTemplateEffectivePreviewLength = (template) => {
  if (!template) {
    return 0;
  }

  const placeholderCount = getPlaceholderCount(template);
  if (!placeholderCount) {
    return template.length;
  }

  const minGuestStandIn = "G".repeat(MIN_GUEST_NAME_CHARS);
  return template.split(GUEST_NAME_PLACEHOLDER).join(minGuestStandIn).length;
};

const wouldExceedDefaultWelcomeMessageLimit = (oldValue, newValue) => {
  const currentLen = getTemplateEffectivePreviewLength(oldValue);
  const newLen = getTemplateEffectivePreviewLength(newValue);

  return newLen > MAX_CHARS && newLen > currentLen;
};

const getRejectedWelcomeMessageCursorPosition = (
  oldValue,
  newValue,
  targetSelectionStart,
  savedSelection
) => {
  const savedStart = savedSelection?.start;
  if (
    typeof savedStart === "number" &&
    savedStart >= 0 &&
    savedStart <= oldValue.length
  ) {
    return savedStart;
  }

  const charsDelta = newValue.length - oldValue.length;
  if (typeof targetSelectionStart === "number") {
    const candidate = targetSelectionStart - Math.max(1, charsDelta);
    if (candidate >= 0 && candidate <= oldValue.length) {
      return candidate;
    }
  }

  let i = 0;
  while (i < oldValue.length && i < newValue.length && oldValue[i] === newValue[i]) {
    i++;
  }
  return i;
};

const fitDefaultWelcomeMessageInsert = (oldValue, insertStart, insertEnd, insertText) => {
  let inserted = insertText;
  let candidate = `${oldValue.slice(0, insertStart)}${inserted}${oldValue.slice(insertEnd)}`;

  while (
    inserted.length > 0 &&
    wouldExceedDefaultWelcomeMessageLimit(oldValue, candidate)
  ) {
    inserted = inserted.slice(0, -1);
    candidate = `${oldValue.slice(0, insertStart)}${inserted}${oldValue.slice(insertEnd)}`;
  }

  if (wouldExceedDefaultWelcomeMessageLimit(oldValue, candidate)) {
    return null;
  }

  return {
    value: candidate,
    cursorPos: insertStart + inserted.length,
  };
};

const rejectDefaultWelcomeMessageChange = (
  target,
  oldValue,
  newValue,
  pendingSelectionRef,
  savedSelectionRef
) => {
  const restorePos = getRejectedWelcomeMessageCursorPosition(
    oldValue,
    newValue,
    target.selectionStart,
    savedSelectionRef?.current
  );

  if (pendingSelectionRef) {
    pendingSelectionRef.current = null;
  }

  if (savedSelectionRef) {
    savedSelectionRef.current = {
      start: restorePos,
      end: restorePos,
    };
  }

  target.value = oldValue;
  target.setSelectionRange(restorePos, restorePos);

  requestAnimationFrame(() => {
    if (target && document.activeElement === target) {
      target.setSelectionRange(restorePos, restorePos);
    }
  });
};

function DefaultWelcomeMessageList({
  templates = [],
  welcomeMessageList = {},
  totalPages = 1,
  templatesLoading = false,
  refetchList,
  listPageNo = 1,
  setListPageNo,
  recordsPerPage = 10,
  hotelName: hotelNameProp,
}) {
  const [allRoomTypesMaster, setAllRoomTypesMaster] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [confirmDeletePublishUnPublish, setConfirmDeletePublishUnPublish] = useState("");
  const [confirmValidate, setConfirmValidate] = useState(false);
  const [deletePublishUnPublishPopUp, setDeletePublishUnPublishPopUp] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editRecordId, setEditRecordId] = useState("");
  const [loader, setLoader] = useState(false);

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
    watch,
    setValue,
    clearErrors
  } = useForm({
    mode: 'onChange'
  });
  const { chainId } = useSelector((state) => state.chainIdReducer);
  const hotelName =
    hotelNameProp || chainId?.navSelectedhotelName?.at(0)?.name || "5555";

  const validPermissionClassName = isValidRole("WELCOME_MESSAGES_MODIFY")
    ? ""
    : "btn-disable-permission";

  const selectedPage = listPageNo;
  const setSelectedPage = setListPageNo || (() => {});
  const totalPageCount = totalPages;
  const [paginationError, setPaginationError] = useState("");
  const [inputPageValue, setInputPageValue] = useState(listPageNo);

  useEffect(() => {
    setInputPageValue(listPageNo);
  }, [listPageNo]);

  const isPublishUnPublishDeletedRef = useRef({
    isPublishUnPublishDeletedModalData: null,
    userConformationModalData: null,
  });
  const welcomeMessageTextareaRef = useRef(null);
  const welcomeMessageSelectionRef = useRef({ start: 0, end: 0 });
  const pendingWelcomeMessageSelectionRef = useRef(null);

  const welcomeMessagePlaceholder = `Dear ${GUEST_NAME_PLACEHOLDER}, Welcome to ${hotelName}`;

  const resetFormDefaults = () => {
    reset({
      roomTypes: [],
      welcomeMessage: welcomeMessagePlaceholder,
    });
    setEditingRow(null);
  };

  const availableRoomTypeOptions = useMemo(
    () => (allRoomTypesMaster?.length ? allRoomTypesMaster : []),
    [allRoomTypesMaster]
  );

  const usedRoomTypes = useMemo(
    () =>
      templates
        .filter(
          (t) =>
            t.id !== editingRow?.id &&
            t.roomType &&
            t.roomType !== "-"
        )
        .map((t) => t.roomType),
    [templates, editingRow]
  );

  const hotelLevelExists = templates.some((t) => t.isHotelLevel);

  const canCreateHotelLevel = !editMode && !hotelLevelExists;

  const finishCreateSuccess = (message) => {
    toast.success(message, { position: "top-right", autoClose: 4000 });
    resetFormDefaults();
    setEditMode(false);
    setEditRecordId("");
    refetchList?.();
    setLoader(false);
  };

  const onSubmit = (data) => {
    if (getTemplateEffectivePreviewLength(data.welcomeMessage) > MAX_CHARS) {
      toast.error(`Welcome message must not exceed ${MAX_CHARS} characters.`, {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    const roomTypes = data.roomTypes || [];

    if (!editMode && !roomTypes.length && hotelLevelExists) {
      toast.error(
        "Hotel-level welcome message already exists. Please select a room type.",
        { position: "top-right", autoClose: 4000 }
      );
      return;
    }

    setLoader(true);
    const chainIdValue = getChainIdLS();

    if (editMode) {
      callAPI(
        `/welcome-message-template/${editRecordId}`,
        "PUT",
        { welcomeMessage: data.welcomeMessage },
        { chainId: chainIdValue }
      )
        .then((response) => {
          finishCreateSuccess(
            response?.message || "Welcome message updated successfully."
          );
        })
        .catch((error) => {
          const errMsg =
            error?.response?.data?.errorMessage ||
            error?.response?.data?.message ||
            error?.message;
          toast.error(errMsg || "Update failed.", {
            position: "top-right",
            autoClose: 4000,
          });
          setLoader(false);
        });
      return;
    }

    const apiPayload = {
      welcomeMessage: data.welcomeMessage,
      chainId: chainIdValue,
      roomTypes,
    };

    callAPI(
      "/welcome-message-template",
      "POST",
      apiPayload,
      { chainId: chainIdValue }
    )
      .then((apiResult) => {
        const createdCount = Array.isArray(apiResult) ? apiResult.length : 1;
        finishCreateSuccess(
          `${createdCount} welcome message template(s) created successfully.`
        );
      })
      .catch((error) => {
        const errMsg =
          error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          error?.message;
        const errCode = error?.response?.data?.errorCode;
        if (errCode === "ERROR_0318" || /already exists/i.test(errMsg || "")) {
          toast.error(
            errMsg || "A template already exists for this chain and room type.",
            { position: "top-right", autoClose: 4000 }
          );
          setLoader(false);
          return;
        }
        toast.error(errMsg || "Create failed.", {
          position: "top-right",
          autoClose: 4000,
        });
        setLoader(false);
      });
  };

  const deletePublishUnPublishPopUpHandler = (row) => {
    setDeletePublishUnPublishPopUp(true);
    isPublishUnPublishDeletedRef.current = {
      isPublishUnPublishDeletedModalData: { row },
      userConformationModalData: null,
    };
  };

  const deletePublishUnPublishHandler = () => {
    setConfirmValidate(true);
    if (confirmDeletePublishUnPublish === "") {
      return;
    }

    const { row } =
      isPublishUnPublishDeletedRef.current?.isPublishUnPublishDeletedModalData || {};
    const confirmPhrase = row?.isHotelLevel
      ? "Delete Hotel Welcome Message"
      : `Delete ${row?.roomType} Welcome Message`;

    if (confirmDeletePublishUnPublish === confirmPhrase) {
      setConfirmValidate(false);
      setLoader(true);
      const chainIdValue = getChainIdLS();
      callAPI(
        `/welcome-message-template/${row.id}`,
        "DELETE",
        undefined,
        { chainId: chainIdValue }
      )
        .then((response) => {
          toast.success(
            response?.message || "Welcome Message Template Deleted Successfully!",
            { position: "top-right", autoClose: 4000 }
          );
          popupCloseHandler();
          refetchList?.();
          setLoader(false);
        })
        .catch((error) => {
          const errMsg =
            error?.response?.data?.errorMessage ||
            error?.response?.data?.message ||
            error?.message;
          toast.error(errMsg || "Delete failed.", {
            position: "top-right",
            autoClose: 4000,
          });
          setLoader(false);
        });
    }
    resetFormDefaults();
  };

  const renderActionIcon = (row) =>
    isValidRole("HOTEL_CHANNELS_MODIFY") ? (
      <>
        <Tooltip title="Edit" arrow>
          <span>
            <MdModeEditOutline
              className="edit-icon-enable"
              onClick={() => {
                setEditRecordId(row.id);
                getRecordForEdit(row);
              }}
            />
          </span>
        </Tooltip>
        <Tooltip title="Delete" arrow>
          <DeleteIcon
            className="delete-icon-enable"
            onClick={() => deletePublishUnPublishPopUpHandler(row)}
          />
        </Tooltip>
      </>
    ) : (
      <>
        <Tooltip title="Don't have permission to edit">
          <span>
            <MdModeEditOutline className="edit-icon-disable" />
          </span>
        </Tooltip>
        <Tooltip title="Don't have permission to delete">
          <DeleteIcon className="delete-icon-disable" />
        </Tooltip>
      </>
    );

  const renderRoomTypeCell = (row) => (
    <p className="mb-0">{row?.roomType || "-"}</p>
  );

  const columns = [
    {
      field: "createdOn",
      headerName: "Created On",
      flex: 1,
      row: true,
      renderCell: (row) => (
        <p className="mb-0">{dayjs(row.createdOn).format("DD MMM YYYY | HH:mm")}</p>
      ),
    },
    {
      field: "roomType",
      headerName: "Room Type",
      flex: 1,
      row: true,
      renderCell: renderRoomTypeCell,
    },
    {
      field: "welcomeMessage",
      headerName: "Welcome Message",
      flex: 1.2,
      row: true,
      renderCell: (row) => (
        <Tooltip title={row?.welcomeMessage}>
          <p
            className={`mb-0 ${row?.welcomeMessage?.length > 10 ? "overflow-ellipsis" : ""}`}
          >
            {row?.welcomeMessage}
          </p>
        </Tooltip>
      ),
    },
    { field: "modifiedBy", headerName: "Modified By", flex: 0.8 },
    {
      field: "actionCell",
      row: true,
      headerName: "Actions",
      flex: 0.5,
      renderCell: renderActionIcon,
      sortDirection: false,
      disableSortIcon: true,
    },
  ];

  useEffect(() => {
    callAPI("/get-room-types", "GET", {}, { chainId: getChainIdLS() })
      .then((res) => setAllRoomTypesMaster(Array.isArray(res) ? res : []))
      .catch((err) => {
        console.warn(err?.message || err);
        setAllRoomTypesMaster([]);
      });
    cancelHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, hotelName]);

  const popupCloseHandler = () => {
    setDeletePublishUnPublishPopUp(false);
    setConfirmDeletePublishUnPublish("");
    setConfirmValidate(false);
  };

  const getRecordForEdit = (row) => {
    setEditingRow(row);
    reset({
      roomTypes: row?.isHotelLevel ? [] : row?.roomTypes || [],
      welcomeMessage: row?.welcomeMessage,
    });
    setEditMode(true);
  };

  const clearAllHandler = () => {
    reset({
      roomTypes: [],
      welcomeMessage: "",
    });
    setEditingRow(null);
    setEditMode(false);
    setEditRecordId("");
  };

  const cancelHandler = () => {
    resetFormDefaults();
    setEditMode(false);
    setEditRecordId("");
  };

  const onPageChange = (event, value) => {
    setInputPageValue(value);
    setPaginationError("");
    setSelectedPage(value);
  };

  useEffect(() => {
    ScrollToTop();
  }, [chainId]);

  const roomTypesDisabled = editMode;
  const selectedRoomTypes = watch("roomTypes") || [];
  const MAX_ROOM_TYPES = 5;
  const welcomeMessageWatch = watch("welcomeMessage") || "";
  const messageLenLive = getTemplateEffectivePreviewLength(welcomeMessageWatch);
  const messageCountStyle =
    messageLenLive > MAX_CHARS
      ? { color: "#dc3545" }
      : messageLenLive >= MAX_CHARS - 10
        ? { color: "#dc3545" }
        : { color: "#6c757d" };
  const isOverLimit = messageLenLive > MAX_CHARS;

  useLayoutEffect(() => {
    const textarea = welcomeMessageTextareaRef.current;
    const pendingSelection = pendingWelcomeMessageSelectionRef.current;

    if (!textarea || !pendingSelection) {
      return;
    }

    textarea.setSelectionRange(pendingSelection.start, pendingSelection.end);
    pendingWelcomeMessageSelectionRef.current = null;
  });

  const resetWelcomeMessageHandler = () => {
    const fallback = editMode
      ? editingRow?.welcomeMessage ?? ""
      : welcomeMessagePlaceholder;
    setValue("welcomeMessage", fallback);
    clearErrors("welcomeMessage");
  };

  return (
    <div className="row config-hotel">
      <div className="col-12 left-column">
        {(loader || templatesLoading) && (
          <div style={{ zIndex: "99999" }} className="circular-progress">
            <CircularProgress />
          </div>
        )}
        <form autoComplete="off" className="hotel-list-form">
          <div className="row">
            <div className="left-column-header">
              <p>
                {editMode
                  ? "Edit Welcome Message Configuration"
                  : "Welcome Message Configuration"}
              </p>
            </div>
            <div className="row pe-0 align-items-start">
              <div className="col-8 mb-3">
                <Controller
                  name="welcomeMessage"
                  control={control}
                  rules={{
                    required: "Welcome Message is required",
                    validate: (value) =>
                      getTemplateEffectivePreviewLength(value) <= MAX_CHARS ||
                      `Welcome message must not exceed ${MAX_CHARS} characters`,
                  }}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      ref={(element) => {
                        field.ref(element);
                        welcomeMessageTextareaRef.current = element;
                      }}
                      id="welcomeMessage"
                      style={{ height: "100%", minHeight: "80px" }}
                      placeholder={welcomeMessagePlaceholder}
                      onSelect={(e) => {
                        welcomeMessageSelectionRef.current = {
                          start: e.target.selectionStart ?? 0,
                          end: e.target.selectionEnd ?? 0,
                        };
                      }}
                      onKeyDown={(e) => {
                        welcomeMessageSelectionRef.current = {
                          start: e.currentTarget.selectionStart ?? 0,
                          end: e.currentTarget.selectionEnd ?? 0,
                        };
                      }}
                      onKeyUp={(e) => {
                        welcomeMessageSelectionRef.current = {
                          start: e.currentTarget.selectionStart ?? 0,
                          end: e.currentTarget.selectionEnd ?? 0,
                        };
                      }}
                      onClick={(e) => {
                        pendingWelcomeMessageSelectionRef.current = null;
                        welcomeMessageSelectionRef.current = {
                          start: e.target.selectionStart ?? 0,
                          end: e.target.selectionEnd ?? 0,
                        };
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const oldValue = field.value || "";
                        const insertStart = e.target.selectionStart ?? 0;
                        const insertEnd = e.target.selectionEnd ?? insertStart;
                        const pasteText = e.clipboardData.getData("text");
                        const fitted = fitDefaultWelcomeMessageInsert(
                          oldValue,
                          insertStart,
                          insertEnd,
                          pasteText
                        );

                        if (!fitted || fitted.value === oldValue) {
                          pendingWelcomeMessageSelectionRef.current = null;
                          e.target.setSelectionRange(insertStart, insertStart);
                          return;
                        }

                        pendingWelcomeMessageSelectionRef.current = {
                          start: fitted.cursorPos,
                          end: fitted.cursorPos,
                        };
                        field.onChange(fitted.value);
                      }}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const oldValue = field.value || "";
                        const charsDelta = newValue.length - oldValue.length;

                        if (!wouldExceedDefaultWelcomeMessageLimit(oldValue, newValue)) {
                          pendingWelcomeMessageSelectionRef.current = null;
                          field.onChange(newValue);
                          return;
                        }

                        if (charsDelta > 1) {
                          const { start: insertStart, end: insertEnd } =
                            welcomeMessageSelectionRef.current;
                          const suffixLen = oldValue.length - insertEnd;
                          const insertedText = newValue.slice(
                            insertStart,
                            newValue.length - suffixLen
                          );
                          const fitted = fitDefaultWelcomeMessageInsert(
                            oldValue,
                            insertStart,
                            insertEnd,
                            insertedText
                          );

                          if (fitted && fitted.value !== oldValue) {
                            pendingWelcomeMessageSelectionRef.current = {
                              start: fitted.cursorPos,
                              end: fitted.cursorPos,
                            };
                            field.onChange(fitted.value);
                            return;
                          }
                        }

                        rejectDefaultWelcomeMessageChange(
                          e.target,
                          oldValue,
                          newValue,
                          pendingWelcomeMessageSelectionRef,
                          welcomeMessageSelectionRef
                        );
                      }}
                    />
                  )}
                />
                <div className="d-flex justify-content-between align-items-start mt-1">
                  <small className="hint-text">
                    Use <code>{GUEST_NAME_PLACEHOLDER}</code> as a placeholder wherever you
                    want the guest's name to appear.
                  </small>
                  <small style={messageCountStyle} className="text-end ms-2 flex-shrink-0">
                    {messageLenLive} / {MAX_CHARS} characters
                  </small>
                </div>
                {errors.welcomeMessage && (
                  <small className="required">{errors.welcomeMessage.message}</small>
                )}
              </div>
              <div className="col-4 form-group-hotel">
                <div className="floating-select-label" style={{ position: "relative" }}>
                  {editMode && editingRow?.isHotelLevel && (
                    <Tooltip
                      title="Hotel-level message — applies to all room types"
                      arrow
                      placement="top"
                    >
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          zIndex: 2,
                          cursor: "not-allowed",
                          background: "rgba(255, 255, 255, 0.45)",
                          borderRadius: "4px",
                        }}
                      />
                    </Tooltip>
                  )}
                  <Controller
                    name="roomTypes"
                    control={control}
                    render={({ field }) => {
                      const selectedCount = field.value?.length || 0;
                      const atLimit = selectedCount >= MAX_ROOM_TYPES;
                      const isOptionUsed = (option) =>
                        usedRoomTypes.includes(option);
                      const isOptionOverLimit = (option) =>
                        atLimit && !(field.value || []).includes(option);
                      const isOptionDisabled = (option) =>
                        isOptionUsed(option) || isOptionOverLimit(option);
                      return (
                        <Autocomplete
                          {...field}
                          className={`multiselectlist ${field?.value?.length > 0 ? "Mui-focused" : ""}`}
                          limitTags={2}
                          disablePortal
                          multiple
                          disabled={roomTypesDisabled}
                          options={availableRoomTypeOptions}
                          getOptionLabel={(option) => option.toString()}
                          getOptionDisabled={isOptionDisabled}
                          renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            const disabled = isOptionDisabled(option);
                            if (!disabled) {
                              return (
                                <li key={key} {...rest}>
                                  {option}
                                </li>
                              );
                            }
                            const title = isOptionUsed(option)
                              ? "Already configured — delete it from the list to reuse"
                              : `You can select up to ${MAX_ROOM_TYPES} room types`;
                            return (
                              <li
                                key={key}
                                {...rest}
                                aria-disabled="true"
                                title={title}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                style={{
                                  ...rest.style,
                                  cursor: "not-allowed",
                                  opacity: 0.5,
                                  pointerEvents: "auto",
                                }}
                              >
                                {option}
                              </li>
                            );
                          }}
                          onChange={(_, newValue) => {
                            if (newValue.length > MAX_ROOM_TYPES) return;
                            const filtered = newValue.filter(
                              (v) =>
                                !isOptionUsed(v) ||
                                (field.value || []).includes(v)
                            );
                            field.onChange(filtered);
                          }}
                          value={field.value || []}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder=""
                              variant="outlined"
                              label="Select Room Type (optional)"
                              helperText={
                                editMode
                                  ? "Room type cannot be changed after creation"
                                  : atLimit
                                    ? `You can select up to ${MAX_ROOM_TYPES} room types`
                                    : canCreateHotelLevel
                                      ? `Leave empty to apply to entire hotel (max ${MAX_ROOM_TYPES} room types)`
                                      : `Hotel-level message already exists — select up to ${MAX_ROOM_TYPES} room types`
                              }
                              InputLabelProps={{ shrink: false }}
                            />
                          )}
                        />
                      );
                    }}
                  />
                </div>
                {!editMode && !canCreateHotelLevel && selectedRoomTypes.length === 0 && (
                  <small className="text-muted d-block mt-1">
                    Select at least one room type. Hotel-level message already configured.
                  </small>
                )}
              </div>
            </div>
            <div className="col-12 text-end">
              <button
                type="button"
                className="clear-btn me-2"
                onClick={resetWelcomeMessageHandler}
              >
                Reset Message
              </button>
              <button
                type="button"
                className="clear-btn me-2"
                onClick={editMode ? cancelHandler : clearAllHandler}
              >
                {editMode ? "Cancel" : "Clear All"}
              </button>
              <button
                type="button"
                disabled={isSubmitting || isOverLimit}
                className={`create-btn ${validPermissionClassName}`}
                onClick={handleSubmit(onSubmit)}
              >
                {editMode ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </form>
        <div className="hotel-list">
          <p>
            <b>Welcome Messages List</b>
          </p>
          <TableWrapper
            data={welcomeMessageList?.welcomeMessageResponse ?? []}
            columns={columns}
            module="publish-unpublish"
            noPagination={true}
          />
          <Custom_Paingation
            selectedPage={selectedPage}
            totalPageCount={totalPageCount}
            paginationError={paginationError}
            inputPageValue={inputPageValue}
            setSelectedPage={setSelectedPage}
            setPaginationError={setPaginationError}
            setInputPageValue={setInputPageValue}
            onPageChange={onPageChange}
          />
        </div>
      </div>
      <DeleteDefaultWelcomeMessage
        deletePublishUnPublishPopUp={deletePublishUnPublishPopUp}
        isPublishUnPublishDeletedRef={isPublishUnPublishDeletedRef}
        confirmDeletePublishUnPublish={confirmDeletePublishUnPublish}
        setConfirmDeletePublishUnPublish={setConfirmDeletePublishUnPublish}
        confirmValidate={confirmValidate}
        popupCloseHandler={popupCloseHandler}
        deletePublishUnPublishHandler={deletePublishUnPublishHandler}
      />
    </div>
  );
}

export default DefaultWelcomeMessageList;
